import { Box, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { toDateTime } from "../../../utils/ValueFormatters";
import { enqueueSnackbar } from "notistack";
import { EbAuthenticatedAppContext } from "@eidsivabredband/app-framework";
import { useContext, useState } from "react";
import { DocumentDto } from "../../../interfaces/IEvent";

interface DocumentSnippetProps {
  doc: any;
  index: number;
  numberOfDocuments: number;
  onUpdateView: () => void;
}

function DocumentSnippet({
  doc,
  index,
  onUpdateView,
  numberOfDocuments,
}: DocumentSnippetProps) {
    const { context } = useContext(EbAuthenticatedAppContext)
    const [confirmDeleteButtonListByDocumentIndex, setConfirmDeleteButtomListByDocumentIndex] = useState<boolean[]>([])

    const handleConfirmDeleteButton = (index: number) => {
        let tempArray = [...confirmDeleteButtonListByDocumentIndex]
        tempArray[index] = !tempArray[index]
        setConfirmDeleteButtomListByDocumentIndex(tempArray)
    }

    const handleDownloadDocument = (doc: any, fileName: any) => {
        enqueueSnackbar(
            <Typography variant="body2">Laster ned dokument...</Typography>,
            { variant: "info" }
        );
        context.restApiClient
            .buildRequest(
                `/api/${doc.eventId}_${doc.documentId}/download`,
            )
            .readResponseAsBlob()
            .getAsync()
            .then((response) => {
                const newBlob = new Blob([response.blob])
                const data = URL.createObjectURL(newBlob)
                const a = document.createElement('a')
                a.href = data
                a.download = fileName
                document.body.appendChild(a)
                a.click()
                a.remove()
                enqueueSnackbar(
                    <Typography variant="body2">Nedlastning vellykket</Typography>,
                    { variant: "success" }
                );
            })
            .catch((err: any) => {
                enqueueSnackbar(
                    <Typography variant="body2">Nedlastning feilet. Prøv igjen eller kontakt systemansvarlig.</Typography>,
                    { variant: "error" }
                );
                throw err
            })
    }

    const handleDeleteDocument = async (doc: DocumentDto, fileName: string) => {
        const response = await context.restApiClient
            .buildRequest(
                `/api/${doc.eventId}_${doc.documentId}/delete`,
            )
            .deleteAsync()

        if (response.error) {
            enqueueSnackbar(
                <Typography variant="body2">Kunne ikke slette dokumentet. Prøv igjen eller kontakt systemansvarlig.</Typography>,
                { variant: "error" }
            );
            throw response.error
        }

        enqueueSnackbar(
            <Typography variant="body2">Dokumentet ble slettet</Typography>,
            { variant: "success" }
        );
        
        setConfirmDeleteButtomListByDocumentIndex(confirmDeleteButtonListByDocumentIndex.map(() => false))
        onUpdateView()
    }

  return (
    <Box key={doc.documentId}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "5px 0"
        }}>
        <Stack
          sx={{
            textAlign: "left",
            textOverflow: "ellipsis",
            overflow: "hidden !important",
            whiteSpace: "nowrap"
          }}>
        <Tooltip
            title={
                doc.comment 
                ? `${doc.comment} - ${doc.fileName} - ${doc.editedBy} - ${toDateTime(new Date(doc.date))}`
                : `${doc.fileName} - ${doc.editedBy} - ${toDateTime(new Date(doc.date))}`
            }
            placement="top"
            arrow    
        >
            <Typography
              variant={"body2"}
              sx={{
                textAlign: "left",
                display: "inline-block",
                textOverflow: "ellipsis",
                overflow: "hidden !important",
                whiteSpace: "nowrap",
                fontWeight: 600
              }}>
                {doc.comment ?? doc.fileName}
            </Typography>
          </Tooltip>
          <Typography
            variant={"body2"}
            sx={{
              textAlign: "left",
              display: "inline-block",
              textOverflow: "ellipsis",
              overflow: "hidden !important",
              whiteSpace: "nowrap"
            }}>
            {toDateTime(new Date(doc.date))}
          </Typography>
        </Stack>
        <Stack direction="row">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleDownloadDocument(doc, doc.fileName)}
          >
            <DownloadIcon />
          </IconButton>
          {confirmDeleteButtonListByDocumentIndex[index] && (
            <IconButton
              size="small"
              onClick={() => handleDeleteDocument(doc, doc.fileName)}
            >
              <CheckOutlinedIcon color="success" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => handleConfirmDeleteButton(index)}
          >
            {confirmDeleteButtonListByDocumentIndex[index] ? (
              <CloseOutlinedIcon color="error" />
            ) : (
              <DeleteOutlinedIcon color="error" />
            )}
          </IconButton>
        </Stack>
      </Box>
      {index !== numberOfDocuments - 1 && <Divider />}
    </Box>
  );
}

export default DocumentSnippet;
